import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Description as ScriptIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  PlayArrow as TriggerIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';

/**
 * FlexScriptList - Left panel showing all FLEX scripts
 *
 * Features:
 * - List of scripts with search filter
 * - Create new script dialog
 * - Select, rename, delete scripts
 * - Show trigger type badges
 */
function FlexScriptList({
  scripts = [],
  selectedId,
  onSelect,
  onCreate,
  onDelete,
  onUpdate,
  onToggleLock,
  currentUserId,
  loading = false,
  initialCreateScriptName = null, // Pre-fill script name and open create dialog
  onInitialCreateHandled = null, // Callback when initial create is handled
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newScriptName, setNewScriptName] = useState('');
  const [newScriptDescription, setNewScriptDescription] = useState('');

  // Handle initial create script name from URL parameter
  useEffect(() => {
    if (initialCreateScriptName) {
      setNewScriptName(initialCreateScriptName);
      setNewDialogOpen(true);
      // Notify parent that we've handled the initial create
      if (onInitialCreateHandled) {
        onInitialCreateHandled();
      }
    }
  }, [initialCreateScriptName, onInitialCreateHandled]);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editScript, setEditScript] = useState(null);
  const [editScriptName, setEditScriptName] = useState('');
  const [editScriptDescription, setEditScriptDescription] = useState('');

  // Filter scripts by search term
  const filteredScripts = scripts.filter((script) =>
    script.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (script.description && script.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle create new script
  const handleCreate = () => {
    if (newScriptName.trim()) {
      onCreate({
        name: newScriptName.trim(),
        description: newScriptDescription.trim(),
        scriptBody: '// New FLEX script\n',
      });
      setNewScriptName('');
      setNewScriptDescription('');
      setNewDialogOpen(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (deleteConfirmId) {
      onDelete(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  // Open edit dialog
  const handleEditOpen = (script) => {
    setEditScript(script);
    setEditScriptName(script.name);
    setEditScriptDescription(script.description || '');
    setEditDialogOpen(true);
  };

  // Handle edit save
  const handleEditSave = () => {
    if (editScript && editScriptName.trim() && onUpdate) {
      onUpdate(editScript.id, {
        name: editScriptName.trim(),
        description: editScriptDescription.trim(),
      });
      setEditDialogOpen(false);
      setEditScript(null);
    }
  };

  // Get trigger type color
  const getTriggerColor = (triggerType) => {
    if (!triggerType) return null;
    const colors = {
      before_receiving: 'info',
      after_receiving: 'success',
      before_putaway: 'warning',
      after_putaway: 'success',
      before_pick: 'info',
      after_pick: 'success',
      before_ship: 'warning',
      after_ship: 'success',
      manual: 'default',
      scheduled: 'secondary',
    };
    return colors[triggerType] || 'default';
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  // Check if current user can manage the lock (creator or script has no owner)
  const canManageLock = (script) => {
    // If no createdBy set, anyone can lock it (claim ownership)
    if (!script.createdBy) return true;
    // Otherwise, only creator can manage lock
    return currentUserId && script.createdBy === currentUserId;
  };

  // Check if current user is the creator
  const isCreator = (script) => {
    return currentUserId && script.createdBy === currentUserId;
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Scripts
        </Typography>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search scripts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        {/* New Script Button */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setNewDialogOpen(true)}
          fullWidth
          size="small"
          sx={{ mt: 1 }}
        >
          New Script
        </Button>
      </Box>

      {/* Script List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading scripts...</Typography>
          </Box>
        ) : filteredScripts.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No scripts match your search' : 'No scripts yet'}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {filteredScripts.map((script) => (
              <ListItem
                key={script.id}
                disablePadding
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {/* Lock/Unlock - creator or unowned scripts */}
                    {canManageLock(script) && (
                      <Tooltip title={script.isLocked ? "Unlock script" : "Lock script"}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleLock && onToggleLock(script.id);
                          }}
                          sx={{
                            opacity: 0.6,
                            '&:hover': { opacity: 1 },
                            color: script.isLocked ? 'warning.main' : 'inherit',
                          }}
                        >
                          {script.isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditOpen(script);
                        }}
                        sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                        disabled={script.isLocked && !isCreator(script)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(script.id);
                        }}
                        sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                        disabled={script.isLocked && !isCreator(script)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <Tooltip
                  title={
                    <Box sx={{ fontSize: 11 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {script.name}
                        {script.isLocked && <LockIcon sx={{ fontSize: 14, color: 'warning.light' }} />}
                      </Typography>
                      {script.description && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          {script.description}
                        </Typography>
                      )}
                      {script.triggerType && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'info.light' }}>
                          Trigger: {script.triggerType.replace(/_/g, ' ')}
                        </Typography>
                      )}
                      <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.2)' }} />
                      <Typography variant="caption" sx={{ display: 'block', color: 'grey.400' }}>
                        Created: {formatDate(script.createdAt)}
                        {script.createdBy && ` by ${script.createdBy}`}
                      </Typography>
                      {script.updatedAt && script.updatedAt !== script.createdAt && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'grey.400' }}>
                          Updated: {formatDate(script.updatedAt)}
                          {script.updatedBy && ` by ${script.updatedBy}`}
                        </Typography>
                      )}
                      {script.isLocked && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.light' }}>
                          Locked by {script.createdBy}
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="right"
                  arrow
                  enterDelay={500}
                >
                  <ListItemButton
                    selected={selectedId === script.id}
                    onClick={() => onSelect(script)}
                    sx={{ pr: 6 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ScriptIcon
                        sx={{
                          color: selectedId === script.id ? 'primary.main' : 'text.secondary',
                          fontSize: 20,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontWeight: selectedId === script.id ? 600 : 400,
                              maxWidth: 100,
                            }}
                          >
                            {script.name}
                          </Typography>
                          {script.isLocked && (
                            <LockIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                          )}
                          {script.triggerType && (
                            <Chip
                              size="small"
                              label={script.triggerType.replace('_', ' ')}
                              color={getTriggerColor(script.triggerType)}
                              sx={{ height: 18, fontSize: 10 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        script.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: 150, display: 'block' }}
                          >
                            {script.description}
                          </Typography>
                        )
                      }
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* New Script Dialog */}
      <Dialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Script</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Script Name"
            value={newScriptName}
            onChange={(e) => setNewScriptName(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., FIRST_LOGIN_HANDLER"
          />
          <TextField
            label="Description (optional)"
            value={newScriptDescription}
            onChange={(e) => setNewScriptDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
            placeholder="Brief description of what this script does..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newScriptName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Script?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this script? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Script Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Script</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Script Name"
            value={editScriptName}
            onChange={(e) => setEditScriptName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description (optional)"
            value={editScriptDescription}
            onChange={(e) => setEditScriptDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
            placeholder="Brief description of what this script does..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={!editScriptName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FlexScriptList;
